/**
 * Toast stack. `toast(level, text)` can be called from anywhere (runtime hooks,
 * engines); the Shell renders <Toaster /> once. Errors stay 7 s, the rest 4.5 s.
 * The two live regions are always in the DOM, so screen readers announce a
 * toast as it is added: errors assertively (role="alert"), the rest politely
 * (role="status"). Errors stack above the rest.
 *
 * Placement: 'top-right' is fixed under the top bar (desk widths). 'stage'
 * sits in the bottom-right corner of the stage column, which ends where the
 * bottom panel begins, lifted above the tablet detail sheet by --sheet-h, so
 * on tablets a toast never lands on the HUD's controls at the top of the stage.
 */
import { create } from 'zustand';
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react';
import { cn } from '@/lib/cn';

export type ToastLevel = 'info' | 'success' | 'warn' | 'error';

interface ToastItem {
  id: number;
  level: ToastLevel;
  text: string;
}

interface ToastState {
  items: ToastItem[];
  push: (level: ToastLevel, text: string) => void;
  dismiss: (id: number) => void;
}

let seq = 0;

export const useToasts = create<ToastState>()((set) => ({
  items: [],
  push: (level, text) => {
    const id = ++seq;
    set((s) => ({ items: [...s.items.slice(-4), { id, level, text }] }));
    window.setTimeout(
      () => set((s) => ({ items: s.items.filter((t) => t.id !== id) })),
      level === 'error' ? 7000 : 4500,
    );
  },
  dismiss: (id) => set((s) => ({ items: s.items.filter((t) => t.id !== id) })),
}));

export function toast(level: ToastLevel, text: string): void {
  useToasts.getState().push(level, text);
}

const ICON = { info: Info, success: CheckCircle2, warn: AlertTriangle, error: XCircle } as const;
/** Read before the text, so the level is not carried by the icon colour alone. */
const LEVEL_WORD = { info: 'Note', success: 'Done', warn: 'Warning', error: 'Error' } as const;
const TONE = {
  info: 'border-hairline-strong text-ink',
  success: 'border-nominal/40 text-ink',
  warn: 'border-caution/50 text-ink',
  error: 'border-warning/50 text-ink',
} as const;
const ICON_TONE = {
  info: 'text-ink-2',
  success: 'text-nominal',
  warn: 'text-caution',
  error: 'text-warning',
} as const;

function ToastCard({ item, onDismiss }: { item: ToastItem; onDismiss: () => void }) {
  const Icon = ICON[item.level];
  return (
    <div
      data-testid="toast"
      className={cn(
        'pointer-events-auto flex items-start gap-2 rounded-panel border bg-raised/95 px-3 py-2 text-xs shadow-elev-2 backdrop-blur',
        TONE[item.level],
      )}
    >
      <Icon aria-hidden className={cn('mt-0.5 h-4 w-4 shrink-0', ICON_TONE[item.level])} />
      <div className="flex-1 leading-snug">
        <span className="sr-only">{LEVEL_WORD[item.level]}: </span>
        {item.text}
      </div>
      <button
        type="button"
        aria-label="Dismiss notification"
        onClick={onDismiss}
        className="rounded-control text-ink-3 hover:text-ink"
      >
        <X aria-hidden className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

export type ToasterPlacement = 'top-right' | 'stage' | 'overview';

const PLACEMENT: Record<ToasterPlacement, string> = {
  overview: 'absolute bottom-[calc(var(--sheet-h,0px)+160px)] right-4 w-[min(360px,calc(100%-2rem))]',
  'top-right': 'fixed right-4 top-14 w-[min(360px,calc(100vw-2rem))]',
  stage: 'absolute bottom-[calc(var(--sheet-h,0px)+0.75rem)] right-3 w-[min(360px,calc(100%-1.5rem))]',
};

export function Toaster({ placement = 'top-right' }: { placement?: ToasterPlacement }) {
  const items = useToasts((s) => s.items);
  const dismiss = useToasts((s) => s.dismiss);
  const errors = items.filter((t) => t.level === 'error');
  const rest = items.filter((t) => t.level !== 'error');
  return (
    <div
      className={cn('pointer-events-none z-50 flex flex-col', PLACEMENT[placement])}
      data-testid="toaster"
      data-placement={placement}
    >
      <div role="alert" aria-live="assertive" className="flex flex-col gap-2">
        {errors.map((t) => (
          <ToastCard key={t.id} item={t} onDismiss={() => dismiss(t.id)} />
        ))}
      </div>
      <div
        role="status"
        aria-live="polite"
        className={cn('flex flex-col gap-2', errors.length > 0 && rest.length > 0 && 'pt-2')}
      >
        {rest.map((t) => (
          <ToastCard key={t.id} item={t} onDismiss={() => dismiss(t.id)} />
        ))}
      </div>
    </div>
  );
}
